import { useLayoutEffect, useRef, useEffect, useCallback } from "react";
import type { MutableRefObject } from "react";
import type { ReactElement } from "react";
import Typography, { type TypographyProps } from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import { formatMoney as formatMoneyDefault } from "@/utils/locale-format";
import { PRICING_PRICE_ANIMATION_MS, PRICING_PRICE_VALUE_FLASH_MS } from "@/components/pricing/pricing-tokens";
import { sanitizeNonNegativeMoney } from "@/components/pricing/sanitize-money";
import { usePrefersReducedMotion } from "@/components/pricing/use-prefers-reduced-motion";

export type AnimatedPriceProps = {
  value: unknown;
  formatValue?: (amount: number) => string;
  typographyProps?: TypographyProps;
  /** Accessible name for the numeric value (e.g. "Grand total"). */
  accessibilityName: string;
};

function easeOutCubic(t: number): number {
  const p = Math.min(1, Math.max(0, t));
  return 1 - (1 - p) ** 3;
}

/**
 * Smoothly interpolates displayed currency using `requestAnimationFrame`.
 * DOM text updates avoid per-frame React state for performance.
 * Brief success tint when the value changes (not on first mount).
 */
export function AnimatedPrice({
  value,
  formatValue = formatMoneyDefault,
  typographyProps,
  accessibilityName,
}: AnimatedPriceProps): ReactElement {
  const theme = useTheme();
  const reducedMotion = usePrefersReducedMotion();
  const displayRef = useRef(0);
  const didMountRef = useRef(false);
  const rafRef = useRef<number | null>(null);
  const flashTimeoutRef = useRef<number | null>(null);
  const visibleRef = useRef<HTMLSpanElement | null>(null);
  const a11yRef = useRef<HTMLSpanElement | null>(null);
  const hostRef = useRef<HTMLElement | null>(null);
  const formatRef = useRef(formatValue);
  formatRef.current = formatValue;

  const target = sanitizeNonNegativeMoney(value);
  const fmt = (n: number) => formatRef.current(sanitizeNonNegativeMoney(n));

  const clearFlashTimeout = useCallback(() => {
    if (flashTimeoutRef.current != null) {
      window.clearTimeout(flashTimeoutRef.current);
      flashTimeoutRef.current = null;
    }
  }, []);

  const triggerValueChangeFlash = useCallback(() => {
    if (reducedMotion) return;
    const el = hostRef.current;
    if (!el) return;
    clearFlashTimeout();
    el.style.transition = `color ${PRICING_PRICE_VALUE_FLASH_MS}ms ease-out`;
    el.style.color = theme.palette.success.main;
    flashTimeoutRef.current = window.setTimeout(() => {
      el.style.color = "";
      el.style.transition = "";
      flashTimeoutRef.current = null;
    }, PRICING_PRICE_VALUE_FLASH_MS);
  }, [clearFlashTimeout, reducedMotion, theme.palette.success.main]);

  const { ref: typographyRefProp, ...restTypographyProps } = typographyProps ?? {};
  const assignHostRef = useCallback(
    (node: HTMLElement | null) => {
      hostRef.current = node;
      if (typeof typographyRefProp === "function") {
        typographyRefProp(node);
      } else if (typographyRefProp && typeof typographyRefProp === "object" && "current" in typographyRefProp) {
        (typographyRefProp as MutableRefObject<HTMLElement | null>).current = node;
      }
    },
    [typographyRefProp]
  );

  useLayoutEffect(() => {
    const visible = visibleRef.current;
    const a11y = a11yRef.current;
    if (!visible || !a11y) return;

    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }

    const applyA11y = (amount: number) => {
      a11y.textContent = `${accessibilityName}: ${fmt(amount)}`;
    };

    if (reducedMotion) {
      didMountRef.current = true;
      displayRef.current = target;
      visible.textContent = fmt(target);
      applyA11y(target);
      return;
    }

    if (!didMountRef.current) {
      didMountRef.current = true;
      displayRef.current = target;
      visible.textContent = fmt(target);
      applyA11y(target);
      return;
    }

    const from = sanitizeNonNegativeMoney(displayRef.current);
    if (from === target) {
      visible.textContent = fmt(target);
      applyA11y(target);
      return;
    }

    triggerValueChangeFlash();

    const start = performance.now();
    const duration = PRICING_PRICE_ANIMATION_MS;

    const tick = (now: number) => {
      const t = duration <= 0 ? 1 : (now - start) / duration;
      if (t >= 1) {
        displayRef.current = target;
        visible.textContent = fmt(target);
        applyA11y(target);
        rafRef.current = null;
        return;
      }
      const eased = easeOutCubic(t);
      const cur = from + (target - from) * eased;
      displayRef.current = cur;
      visible.textContent = fmt(cur);
      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      clearFlashTimeout();
    };
  }, [target, reducedMotion, accessibilityName, triggerValueChangeFlash, clearFlashTimeout]);

  useEffect(() => {
    return () => {
      didMountRef.current = false;
      clearFlashTimeout();
    };
  }, [clearFlashTimeout]);

  return (
    <Typography
      ref={assignHostRef}
      component="span"
      {...restTypographyProps}
      sx={{
        ...restTypographyProps?.sx,
        fontVariantNumeric: "tabular-nums",
      }}
    >
      <span ref={visibleRef} aria-hidden="true" />
      <span
        ref={a11yRef}
        className="Mui-visually-hidden"
        style={{
          position: "absolute",
          width: 1,
          height: 1,
          padding: 0,
          margin: -1,
          overflow: "hidden",
          clip: "rect(0, 0, 0, 0)",
          whiteSpace: "nowrap",
          border: 0,
        }}
      />
    </Typography>
  );
}
